import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { format, isPast } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import StartEndDateRangeFields from '../StartEndDateRangeFields'

import { rightSelector } from '../../../modules/selectors/rights'

import { END_DATE, START_DATE } from '../StartEndDateRangeFields/fields'
import { DATES_FORMAT_LONG } from '../../../constants/dates'

import useDrawerFormStyles from '../../../styles/common/drawerForms'

const StartAndEndDatesLimitedByRightSection = ({
  formik,
  dayContentRenderer,
  entityName = 'ad set',
  adAccountTimezone
}) => {
  const drawerFormClasses = useDrawerFormStyles()

  const { t } = useTranslation()

  const right = useSelector(rightSelector)

  const { [START_DATE]: rightStartDate, [END_DATE]: rightEndDate } = right

  const formattedRightStartDate = rightStartDate ? format(rightStartDate, DATES_FORMAT_LONG) : null
  const formattedRightEndDate = rightEndDate ? format(rightEndDate, DATES_FORMAT_LONG) : null

  const isRightStartDateInThePast = useMemo(() => {
    if (!rightStartDate) return false

    // If adAccountTimezone is provided, check if the start date is in the past
    // according to the ad account timezone, not user's local timezone
    if (adAccountTimezone) {
      const nowInAdAccountTimezone = toZonedTime(new Date(), adAccountTimezone)
      return new Date(rightStartDate) < nowInAdAccountTimezone
    }

    // Fallback to existing logic (user's local timezone)
    return isPast(new Date(rightStartDate))
  }, [rightStartDate, adAccountTimezone])

  const minStartDate = useMemo(() => {
    if (isRightStartDateInThePast) {
      // If adAccountTimezone is provided, use current time in ad account timezone
      if (adAccountTimezone) {
        return toZonedTime(new Date(), adAccountTimezone)
      }
      // Fallback to existing logic (user's local timezone)
      return new Date()
    }
    return rightStartDate
  }, [isRightStartDateInThePast, rightStartDate, adAccountTimezone])

  if (!rightStartDate || !rightEndDate) {
    // If the right does not have start or end date, we just show the fields without any limits
    return (
      <section className={drawerFormClasses.section}>
        <div className={drawerFormClasses.sectionTitle}>{t('Start and end dates')}</div>
        <p>{t(`Please enter the start and end date for this ${entityName}.`)}</p>
        <StartEndDateRangeFields formik={formik} dayContentRenderer={dayContentRenderer} />
      </section>
    )
  } else {
    return (
      <section className={drawerFormClasses.section}>
        <div className={drawerFormClasses.sectionTitle}>{t('Start and end dates')}</div>
        <p>
          {t(
            `Please enter the start and end date for this ${entityName}. You can select any dates within the agreed period from ${formattedRightStartDate} to ${formattedRightEndDate}.`
          )}
        </p>
        <StartEndDateRangeFields
          formik={formik}
          minDate={minStartDate}
          maxDate={rightEndDate}
          dayContentRenderer={dayContentRenderer}
        />
      </section>
    )
  }
}

StartAndEndDatesLimitedByRightSection.propTypes = {
  formik: PropTypes.object.isRequired,
  dayContentRenderer: PropTypes.func,
  entityName: PropTypes.string,
  adAccountTimezone: PropTypes.string
}

export default StartAndEndDatesLimitedByRightSection
